import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { I18n } from 'aws-amplify'
import { string, object, number } from 'yup'

import { Button, Flex, Input, Label, Text, Form, Slot } from '@/primitives'

import { Dialog, Select } from '@/elements'

import { showBanner } from '@/slices/util'
import { fetchOrganizations } from '@/slices/management/organization'
import { fetchRoles } from '@/slices/management/role'
import {
  createSensorEnrollment,
  updateSensorEnrollment,
  getSensorEnrollment
} from '@/api/management/sensorEnrollment'

import {
  getOrganizations,
  getRoles,
  getOrganizationsLoading,
  getIsLoading
} from '@/reducers/selectors'

import Strings from '../Strings'

const sensorLinkingSchema = object().shape({
  sensorIds: string().trim().required(I18n.get('Please enter sensor IDs')),
  organizationId: string().required(I18n.get('Please select an organization')),
  roleName: string().required(I18n.get('Please select a role')),
  maxSignUps: number()
    .min(1, I18n.get('Maximum sign-ups must be at least 1'))
    .required(I18n.get('Please enter maximum number of sign-ups'))
})

export default function UpsertEnrollmentModal({ onSuccess }) {
  const dispatch = useDispatch()
  const history = useHistory()
  const { enrollmentId } = useParams()
  const strings = Strings()

  const [sensorIds, setSensorIds] = useState('')
  const [organizationId, setOrganizationId] = useState('')
  const [roleName, setRoleName] = useState('')
  const [maxSignUps, setMaxSignUps] = useState(5)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingEnrollment, setIsLoadingEnrollment] = useState(false)

  const organizations = getOrganizations()
  const roles = getRoles()
  const organizationsLoading = getOrganizationsLoading()
  const isLoading = getIsLoading()

  const isEditMode = !!enrollmentId

  useEffect(() => {
    dispatch(fetchOrganizations({ page: 1, pageSize: 1000 }))
    dispatch(fetchRoles())
  }, [dispatch])

  useEffect(() => {
    if (isEditMode) {
      loadEnrollment()
    }
  }, [enrollmentId, isEditMode])

  const loadEnrollment = async () => {
    setIsLoadingEnrollment(true)
    try {
      const { enrollment, error } = await getSensorEnrollment({ enrollmentId })

      if (error) {
        dispatch(
          showBanner({
            show: true,
            type: 'error',
            message: error
          })
        )
        handleClose()
      } else {
        setSensorIds(enrollment.deviceIds?.join('\n') || '')
        setOrganizationId(enrollment.organizationId)
        setRoleName(enrollment.roleName)
        setMaxSignUps(enrollment.maxSignups)
      }
    } catch (error) {
      dispatch(
        showBanner({
          show: true,
          type: 'error',
          message: error?.message || I18n.get('An error occurred')
        })
      )
      handleClose()
    } finally {
      setIsLoadingEnrollment(false)
    }
  }

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()
      setIsSubmitting(true)

      try {
        const formData = {
          sensorIds,
          organizationId,
          roleName,
          maxSignUps
        }

        await sensorLinkingSchema.validate(formData)

        const deviceIds = formData.sensorIds
          .split(/[,\n\r\t\s]+/)
          .map(id => id.trim())
          .filter(id => id.length > 0)

        const payload = {
          organizationId: formData.organizationId,
          roleName: formData.roleName,
          maxSignups: parseInt(formData.maxSignUps),
          deviceIds
        }

        const apiCall = isEditMode
          ? updateSensorEnrollment({ enrollmentId, ...payload })
          : createSensorEnrollment(payload)

        const { error } = await apiCall

        if (error) {
          dispatch(
            showBanner({
              show: true,
              type: 'error',
              message: error
            })
          )
          setIsSubmitting(false)
          return
        }

        dispatch(
          showBanner({
            show: true,
            type: 'success',
            message: isEditMode
              ? I18n.get('Enrollment updated successfully')
              : I18n.get(`Successfully linked sensors to organization`)
          })
        )

        onSuccess?.()
        handleClose()
      } catch (error) {
        dispatch(
          showBanner({
            show: true,
            type: 'error',
            message: error?.message || I18n.get('An error occurred')
          })
        )
        setIsSubmitting(false)
      }
    },
    [
      dispatch,
      isEditMode,
      enrollmentId,
      onSuccess,
      sensorIds,
      organizationId,
      roleName,
      maxSignUps
    ]
  )

  const handleClose = () => {
    history.push('/admin/ops')
  }

  const organizationOptions = Object.values(organizations.result || []).map(
    org => ({
      value: org.id,
      label: org.name
    })
  )

  const roleOptions = Object.values(roles).map(role => ({
    value: role.name,
    label: role.name
  }))

  return (
    <Dialog type='modal' open={true} onOpenChange={handleClose}>
      <Slot name='title'>
        <Text size='300' fontWeight={700}>
          {isEditMode
            ? I18n.get('Edit Sensor Enrollment')
            : strings.opsPageHeader}
        </Text>
      </Slot>
      <Slot name='content'>
        {isLoadingEnrollment ? (
          <Text>{I18n.get('Loading...')}</Text>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Flex axisGap={200} direction='column'>
              <Text variant='body' style={{ marginBottom: '0.5rem' }}>
                {strings.opsPageDescription}
              </Text>

              <Label>
                <Text variant='page' tone={700}>
                  {strings.sensorIdsLabel}
                </Text>
                <Input
                  as='textarea'
                  name='sensorIds'
                  placeholder={strings.sensorIdsPlaceholder}
                  value={sensorIds}
                  onChange={e => setSensorIds(e.target.value)}
                  rows={4}
                  required
                />
                <Text variant='caption' color='muted'>
                  {strings.sensorIdsHelp}
                </Text>
              </Label>

              <Label>
                <Text variant='page' tone={700}>
                  {strings.organizationLabel}
                </Text>
                <Select
                  name='organizationId'
                  placeholder={strings.selectOrganizationPlaceholder}
                  options={organizationOptions}
                  value={organizationId}
                  onChange={option => setOrganizationId(option?.value || '')}
                  isLoading={organizationsLoading}
                />
              </Label>

              <Label>
                <Text variant='page' tone={700}>
                  {strings.roleLabel}
                </Text>
                <Select
                  name='roleName'
                  placeholder={strings.selectRolePlaceholder}
                  options={roleOptions}
                  value={roleName}
                  onChange={option => setRoleName(option?.value || '')}
                  isLoading={isLoading}
                />
              </Label>

              <Label>
                <Text variant='page' tone={700}>
                  {strings.maxSignUpsLabel}
                </Text>
                <Input
                  type='number'
                  name='maxSignUps'
                  placeholder={strings.maxSignUpsPlaceholder}
                  value={maxSignUps}
                  onChange={e => setMaxSignUps(parseInt(e.target.value) || 1)}
                  min={1}
                  required
                />
                <Text variant='caption' color='muted'>
                  {strings.maxSignUpsHelp}
                </Text>
              </Label>
            </Flex>

            <Flex
              alignMainAxis='flex-end'
              axisGap={100}
              style={{ marginTop: '1.5rem' }}
            >
              <Button
                type='button'
                variant='secondary'
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {strings.cancel}
              </Button>
              <Button
                type='submit'
                variant='primary'
                disabled={
                  isSubmitting ||
                  organizationsLoading ||
                  isLoading ||
                  !sensorIds.trim() ||
                  !organizationId ||
                  !roleName
                }
              >
                {isEditMode ? I18n.get('Update') : strings.linkSensors}
              </Button>
            </Flex>
          </Form>
        )}
      </Slot>
    </Dialog>
  )
}
